import type { PageGuide } from '../../components/guide/guideTypes'

export const coreGuides: PageGuide[] = [
  {
    match: ['/dashboard', '/'],
    title: { ar: 'لوحة التحكم', en: 'Dashboard', fr: 'Tableau de bord' },
    summary: {
      ar: 'مركز قيادة أعمالك: ملخص المبيعات، أرصدة العملاء، والتنبيهات في نظرة واحدة.',
      en: 'Your business command center: sales, balances, and alerts at a glance.',
      fr: 'Centre de pilotage : ventes, soldes et alertes en un coup d’œil.',
    },
    emoji: '🏠',
    accent: '#0d9488',
    gallery: [
      {
        src: '/help/dashboard/overview.svg',
        motif: 'dashboard',
        caption: { ar: 'نظرة عامة على المؤشرات', en: 'KPIs overview', fr: 'Vue KPI' },
        highlight: { ar: 'الودجات قابلة للتخصيص', en: 'Widgets are customizable', fr: 'Widgets personnalisables' },
      },
    ],
    sections: [
      {
        title: { ar: 'البداية السريعة', en: 'Quick start', fr: 'Démarrage rapide' },
        steps: [
          {
            title: { ar: 'اقرأ المؤشرات', en: 'Read the KPIs', fr: 'Lire les KPI' },
            body: {
              ar: 'البطاقات العلوية تعرض المبيعات والمشتريات والأرصدة حسب الفترة المحددة.',
              en: 'Top cards show sales, purchases, and balances for the selected period.',
              fr: 'Les cartes affichent ventes, achats et soldes pour la période.',
            },
            shot: {
              src: '/help/dashboard/kpis.svg',
              motif: 'dashboard',
              caption: { ar: 'بطاقات المؤشرات', en: 'KPI cards', fr: 'Cartes KPI' },
            },
          },
          {
            title: { ar: 'خصص العرض', en: 'Customize the view', fr: 'Personnaliser' },
            body: {
              ar: 'يمكنك إضافة أو إخفاء ودجات حسب احتياجك اليومي.',
              en: 'Add or hide widgets to match your daily workflow.',
              fr: 'Ajoutez ou masquez des widgets selon votre routine.',
            },
            shot: {
              motif: 'settings',
              caption: { ar: 'تخصيص الودجات', en: 'Widget layout', fr: 'Disposition' },
            },
          },
        ],
      },
    ],
    tips: [
      {
        ar: 'استخدم القائمة الجانبية أو Ctrl+K للانتقال السريع لأي شاشة.',
        en: 'Use the sidebar or Ctrl+K to jump to any screen.',
        fr: 'Menu latéral ou Ctrl+K pour naviguer vite.',
      },
    ],
  },
  {
    match: ['/customers', '/customers*'],
    title: { ar: 'العملاء', en: 'Customers', fr: 'Clients' },
    summary: {
      ar: 'سجّل عملاءك، تابع أرصدتهم، وافتح كشف حساب أو فاتورة مباشرة منهم.',
      en: 'Register customers, track balances, open statements or invoices from here.',
      fr: 'Enregistrez vos clients, soldes, relevés et factures.',
    },
    emoji: '👥',
    accent: '#0891b2',
    gallery: [
      {
        src: '/help/customers/list.svg',
        motif: 'list',
        caption: { ar: 'قائمة العملاء', en: 'Customer list', fr: 'Liste clients' },
        highlight: { ar: 'بحث سريع بالاسم أو الهاتف', en: 'Search by name or phone', fr: 'Recherche nom/téléphone' },
      },
    ],
    sections: [
      {
        title: { ar: 'إدارة العملاء', en: 'Managing customers', fr: 'Gérer les clients' },
        steps: [
          {
            title: { ar: 'أضف عميلاً', en: 'Add a customer', fr: 'Ajouter un client' },
            body: {
              ar: 'اضغط إضافة عميل، أدخل الاسم وبيانات التواصل والحساب المرتبط إن لزم.',
              en: 'Click Add Customer, enter name, contacts, and linked account if needed.',
              fr: 'Cliquez Ajouter, saisissez nom, contacts et compte lié.',
            },
            shot: {
              src: '/help/customers/form.svg',
              motif: 'form',
              caption: { ar: 'نموذج العميل', en: 'Customer form', fr: 'Formulaire client' },
            },
          },
          {
            title: { ar: 'تابع الرصيد', en: 'Track balance', fr: 'Suivre le solde' },
            body: {
              ar: 'من القائمة ترى الرصيد الحالي. افتح كشف الحساب لمتابعة الفواتير والتحصيل.',
              en: 'The list shows current balance. Open the statement for invoices and collections.',
              fr: 'La liste montre le solde. Ouvrez le relevé pour factures et encaissements.',
            },
            shot: {
              motif: 'payment',
              caption: { ar: 'الرصيد وكشف الحساب', en: 'Balance & statement', fr: 'Solde et relevé' },
            },
          },
        ],
      },
    ],
  },
  {
    match: ['/vendors', '/vendors*'],
    title: { ar: 'الموردين', en: 'Vendors', fr: 'Fournisseurs' },
    summary: {
      ar: 'إدارة مورديك وارتباطهم بفواتير الشراء والمدفوعات.',
      en: 'Manage vendors linked to purchase invoices and payments.',
      fr: 'Gérez les fournisseurs liés aux achats et paiements.',
    },
    emoji: '🏭',
    accent: '#0f766e',
    sections: [
      {
        title: { ar: 'العمل اليومي', en: 'Daily work', fr: 'Travail quotidien' },
        steps: [
          {
            title: { ar: 'تسجيل مورد', en: 'Register a vendor', fr: 'Enregistrer' },
            body: {
              ar: 'أضف الاسم والهاتف والعنوان ورقم الضريبة إن وُجد، ثم احفظ.',
              en: 'Add name, phone, address, and tax number if any, then save.',
              fr: 'Ajoutez nom, téléphone, adresse et n° fiscal si besoin.',
            },
            shot: { motif: 'form', caption: { ar: 'بيانات المورد', en: 'Vendor details', fr: 'Détails' } },
          },
          {
            title: { ar: 'الربط بالشراء', en: 'Link to purchases', fr: 'Lien achats' },
            body: {
              ar: 'عند إنشاء فاتورة شراء اختر المورد من هذه القائمة ليظهر رصيده تلقائياً.',
              en: 'When creating a purchase invoice, pick the vendor so the balance posts correctly.',
              fr: 'Lors d’un achat, sélectionnez le fournisseur pour le solde.',
            },
            shot: { motif: 'list', caption: { ar: 'اختيار المورد', en: 'Vendor selection', fr: 'Sélection' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/items', '/items*', '/items-directory'],
    title: { ar: 'الأصناف', en: 'Items', fr: 'Articles' },
    summary: {
      ar: 'تعريف الأصناف وأسعارها ووحداتها هو أساس الفواتير والمخزون.',
      en: 'Item setup (prices, units) powers invoices and inventory.',
      fr: 'Les articles (prix, unités) alimentent factures et stock.',
    },
    emoji: '📦',
    accent: '#0369a1',
    gallery: [
      {
        src: '/help/items/list.svg',
        motif: 'list',
        caption: { ar: 'دليل الأصناف', en: 'Items catalog', fr: 'Catalogue' },
      },
    ],
    sections: [
      {
        title: { ar: 'إعداد صنف', en: 'Set up an item', fr: 'Créer un article' },
        steps: [
          {
            title: { ar: 'البيانات الأساسية', en: 'Basic data', fr: 'Données de base' },
            body: {
              ar: 'الاسم، الفئة، الباركود، والوحدة الأساسية. أضف وحدات إضافية للبيع/الشراء إن لزم.',
              en: 'Name, category, barcode, and base unit. Add extra sale/purchase units if needed.',
              fr: 'Nom, catégorie, code-barres, unité de base. Unités supplémentaires si besoin.',
            },
            shot: {
              src: '/help/items/form.svg',
              motif: 'form',
              caption: { ar: 'بطاقة الصنف', en: 'Item card', fr: 'Fiche article' },
            },
          },
          {
            title: { ar: 'الأسعار والمخزون', en: 'Prices & stock', fr: 'Prix et stock' },
            body: {
              ar: 'حدد سعر البيع والتكلفة. المخزون يظهر حسب المخزن بعد الحركات.',
              en: 'Set selling price and cost. Stock appears per store after movements.',
              fr: 'Définissez prix de vente et coût. Stock par dépôt après mouvements.',
            },
            shot: {
              motif: 'dashboard',
              caption: { ar: 'الأسعار والأرصدة', en: 'Prices & balances', fr: 'Prix et stocks' },
            },
          },
        ],
      },
    ],
    tips: [
      {
        ar: 'من «تعديل أسعار الأصناف» يمكنك تحديث أسعار كثيرة دفعة واحدة.',
        en: 'Use Items Prices Editor to update many prices at once.',
        fr: 'L’éditeur de prix met à jour plusieurs articles d’un coup.',
      },
    ],
  },
  {
    match: ['/invoices', '/invoices*', '/pos'],
    title: { ar: 'فواتير المبيعات ونقاط البيع', en: 'Sales invoices & POS', fr: 'Factures & caisse' },
    summary: {
      ar: 'أنشئ فاتورة مبيعات أو استخدم نقطة البيع للبيع السريع مع الطباعة والتحصيل.',
      en: 'Create sales invoices or use POS for quick sell, print, and collect.',
      fr: 'Créez des factures ou utilisez la caisse pour vendre vite.',
    },
    emoji: '🧾',
    accent: '#b45309',
    gallery: [
      {
        src: '/help/invoices/new.svg',
        motif: 'form',
        caption: { ar: 'فاتورة مبيعات جديدة', en: 'New sales invoice', fr: 'Nouvelle facture' },
        highlight: { ar: 'أضف الأصناف ثم ادفع', en: 'Add items then pay', fr: 'Articles puis paiement' },
      },
    ],
    sections: [
      {
        title: { ar: 'إنشاء فاتورة', en: 'Create an invoice', fr: 'Créer une facture' },
        steps: [
          {
            title: { ar: 'اختر العميل والمستودع', en: 'Pick customer & store', fr: 'Client et dépôt' },
            body: {
              ar: 'حدد العميل (أو نقدي) والمخزن والتاريخ. هذه البيانات تؤثر على المخزون والحسابات.',
              en: 'Select customer (or cash), store, and date — they drive stock and accounting.',
              fr: 'Client (ou comptant), dépôt et date influencent stock et compta.',
            },
            shot: {
              motif: 'form',
              caption: { ar: 'رأس الفاتورة', en: 'Invoice header', fr: 'En-tête' },
              highlight: { ar: 'العميل + المخزن', en: 'Customer + store', fr: 'Client + dépôt' },
            },
          },
          {
            title: { ar: 'أضف الأصناف', en: 'Add line items', fr: 'Lignes articles' },
            body: {
              ar: 'ابحث بالاسم أو الباركود، أدخل الكمية والسعر، راقب الإجمالي والضريبة.',
              en: 'Search by name or barcode, enter qty and price, watch totals and tax.',
              fr: 'Recherche nom/code-barres, qté et prix, totaux et taxe.',
            },
            shot: {
              src: '/help/invoices/lines.svg',
              motif: 'list',
              caption: { ar: 'بنود الفاتورة', en: 'Invoice lines', fr: 'Lignes' },
            },
          },
          {
            title: { ar: 'الدفع والحفظ', en: 'Pay & save', fr: 'Payer et enregistrer' },
            body: {
              ar: 'اختر طريقة الدفع (نقدي/شبكة/آجل)، احفظ، ثم اطبع إن رغبت.',
              en: 'Choose payment method (cash/card/credit), save, then print if needed.',
              fr: 'Mode de paiement, enregistrement, puis impression.',
            },
            shot: {
              src: '/help/invoices/payment.svg',
              motif: 'payment',
              caption: { ar: 'تحصيل الفاتورة', en: 'Collect payment', fr: 'Encaissement' },
            },
          },
        ],
      },
    ],
    tips: [
      {
        ar: 'في نقطة البيع (POS) استخدم اختصارات لوحة المفاتيح لتسريع البيع.',
        en: 'In POS, keyboard shortcuts speed up selling.',
        fr: 'En caisse, les raccourcis clavier accélèrent la vente.',
      },
    ],
  },
  {
    match: ['/purchase-invoices', '/purchase-invoices*'],
    title: { ar: 'فواتير الشراء', en: 'Purchase invoices', fr: 'Factures d’achat' },
    summary: {
      ar: 'تسجيل مشترياتك يزيد المخزون ويرفع رصيد المورد. أدخل البنود بدقة قبل الحفظ.',
      en: 'Purchases increase stock and vendor balance. Enter lines carefully before saving.',
      fr: 'Les achats augmentent le stock et le solde fournisseur.',
    },
    emoji: '🛒',
    accent: '#047857',
    gallery: [
      {
        src: '/help/purchase-invoices/new.svg',
        motif: 'form',
        caption: { ar: 'فاتورة شراء جديدة', en: 'New purchase invoice', fr: 'Nouvel achat' },
      },
    ],
    sections: [
      {
        title: { ar: 'دورة الشراء', en: 'Purchase flow', fr: 'Flux d’achat' },
        steps: [
          {
            title: { ar: 'المورد والمخزن', en: 'Vendor & store', fr: 'Fournisseur et dépôt' },
            body: {
              ar: 'اختر المورد والمخزن الذي ستدخل إليه البضاعة ورقم فاتورة المورد إن وُجد.',
              en: 'Pick vendor, receiving store, and vendor invoice number if available.',
              fr: 'Fournisseur, dépôt de réception et n° facture fournisseur.',
            },
            shot: {
              motif: 'form',
              caption: { ar: 'رأس فاتورة الشراء', en: 'Purchase header', fr: 'En-tête achat' },
            },
          },
          {
            title: { ar: 'الأصناف والكميات', en: 'Items & quantities', fr: 'Articles et quantités' },
            body: {
              ar: 'أضف كل صنف مع الكمية وسعر التكلفة. التكلفة تؤثر على متوسط المخزون والتقارير.',
              en: 'Add each item with qty and cost. Cost affects inventory average and reports.',
              fr: 'Chaque article avec qté et coût — impact sur le stock moyen.',
            },
            shot: {
              src: '/help/purchase-invoices/lines.svg',
              motif: 'list',
              caption: { ar: 'بنود الشراء', en: 'Purchase lines', fr: 'Lignes d’achat' },
              highlight: { ar: 'سعر التكلفة مهم', en: 'Cost price matters', fr: 'Le coût compte' },
            },
          },
          {
            title: { ar: 'الحفظ والترحيل', en: 'Save & post', fr: 'Enregistrer' },
            body: {
              ar: 'بعد المراجعة احفظ الفاتورة. سيتم تحديث المخزون وحساب المورد تلقائياً.',
              en: 'After review, save — stock and vendor account update automatically.',
              fr: 'Après contrôle, enregistrez — stock et compte fournisseur mis à jour.',
            },
            shot: {
              motif: 'approve',
              caption: { ar: 'تأكيد الشراء', en: 'Confirm purchase', fr: 'Confirmer l’achat' },
            },
          },
        ],
      },
    ],
    tips: [
      {
        ar: 'يمكنك ربط فاتورة الشراء بأمر شراء سابق لتقليل الإدخال اليدوي.',
        en: 'Link to a purchase order when available to reduce re-typing.',
        fr: 'Liez à une commande d’achat pour gagner du temps.',
      },
    ],
  },
  {
    match: ['/journal', '/journal*'],
    title: { ar: 'قيود اليومية', en: 'Journal entries', fr: 'Écritures' },
    summary: {
      ar: 'تسجيل القيود المحاسبية اليدوية مع توازن المدين والدائن.',
      en: 'Manual accounting entries — debits must equal credits.',
      fr: 'Écritures manuelles — débit = crédit.',
    },
    emoji: '📒',
    accent: '#1d4ed8',
    sections: [
      {
        title: { ar: 'إنشاء قيد', en: 'Create an entry', fr: 'Créer une écriture' },
        steps: [
          {
            title: { ar: 'رأس القيد', en: 'Entry header', fr: 'En-tête' },
            body: {
              ar: 'حدد التاريخ والبيان والمرجع. أضف أسطر مدين ودائن للحسابات.',
              en: 'Set date, description, and reference. Add debit and credit lines.',
              fr: 'Date, libellé, référence. Lignes débit et crédit.',
            },
            shot: { motif: 'form', caption: { ar: 'قيد يومية', en: 'Journal form', fr: 'Formulaire' } },
          },
          {
            title: { ar: 'تحقق من التوازن', en: 'Check balance', fr: 'Équilibrer' },
            body: {
              ar: 'لن يُحفظ القيد إن لم يتساوَ مجموع المدين مع الدائن.',
              en: 'The entry will not save unless debits equal credits.',
              fr: 'Enregistrement impossible si débit ≠ crédit.',
            },
            shot: { motif: 'approve', caption: { ar: 'توازن القيد', en: 'Balanced entry', fr: 'Écriture équilibrée' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/vouchers', '/vouchers*'],
    title: { ar: 'السندات', en: 'Vouchers', fr: 'Pièces' },
    summary: {
      ar: 'سندات القبض والصرف لتسجيل التحصيل والدفع وربطها بالعملاء أو الموردين.',
      en: 'Receipt and payment vouchers for collections and disbursements.',
      fr: 'Reçus et paiements liés clients/fournisseurs.',
    },
    emoji: '💵',
    accent: '#15803d',
    sections: [
      {
        title: { ar: 'سند سريع', en: 'Quick voucher', fr: 'Pièce rapide' },
        steps: [
          {
            title: { ar: 'نوع السند', en: 'Voucher type', fr: 'Type' },
            body: {
              ar: 'اختر قبض أو صرف، ثم الطرف (عميل/مورد/حساب) والمبلغ والطريقة.',
              en: 'Pick receipt or payment, then party (customer/vendor/account), amount, and method.',
              fr: 'Encaissement ou décaissement, tiers, montant et mode.',
            },
            shot: { motif: 'payment', caption: { ar: 'شاشة السند', en: 'Voucher screen', fr: 'Écran pièce' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/reports', '/reports*'],
    title: { ar: 'التقارير', en: 'Reports', fr: 'Rapports' },
    summary: {
      ar: 'كل التقارير المالية والمخزنية من مكان واحد: فلتر ← عرض ← طباعة/تصدير.',
      en: 'All financial and inventory reports in one place: filter → view → print/export.',
      fr: 'Tous les rapports : filtrer → afficher → imprimer/exporter.',
    },
    emoji: '📈',
    accent: '#7c2d12',
    sections: [
      {
        title: { ar: 'قراءة تقرير', en: 'Run a report', fr: 'Lancer un rapport' },
        steps: [
          {
            title: { ar: 'اختر التقرير', en: 'Choose the report', fr: 'Choisir' },
            body: {
              ar: 'من فهرس التقارير افتح التقرير المطلوب (مبيعات، أرصدة، مخزون…).',
              en: 'From the report index open the report you need (sales, balances, stock…).',
              fr: 'Depuis l’index, ouvrez le rapport souhaité.',
            },
            shot: { motif: 'dashboard', caption: { ar: 'فهرس التقارير', en: 'Reports index', fr: 'Index' } },
          },
          {
            title: { ar: 'فلتر ثم صدّر', en: 'Filter then export', fr: 'Filtrer puis exporter' },
            body: {
              ar: 'حدد الفترة والفرع أو الحساب، اعرض النتيجة، ثم اطبع أو صدّر Excel.',
              en: 'Set period and branch/account, view results, then print or export Excel.',
              fr: 'Période et filtres, affichage, puis impression ou Excel.',
            },
            shot: { motif: 'print', caption: { ar: 'التصدير', en: 'Export', fr: 'Export' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/settings', '/settings*', '/companies*', '/users', '/roles'],
    title: { ar: 'الإعدادات والصلاحيات', en: 'Settings & permissions', fr: 'Paramètres' },
    summary: {
      ar: 'ضبط الشركة، المستخدمين، والصلاحيات يحكم ما يراه كل مستخدم في النظام.',
      en: 'Company setup, users, and roles control what each user can see and do.',
      fr: 'Société, utilisateurs et rôles contrôlent l’accès.',
    },
    emoji: '⚙️',
    accent: '#475569',
    sections: [
      {
        title: { ar: 'أفضل ممارسة', en: 'Best practice', fr: 'Bonne pratique' },
        steps: [
          {
            title: { ar: 'أكمل بيانات الشركة', en: 'Complete company profile', fr: 'Fiche société' },
            body: {
              ar: 'الاسم، الشعار، الضريبة، والعام المالي — تظهر على الفواتير المطبوعة.',
              en: 'Name, logo, tax, and fiscal year — they appear on printed invoices.',
              fr: 'Nom, logo, taxe et exercice — visibles sur les factures.',
            },
            shot: { motif: 'settings', caption: { ar: 'إعدادات الشركة', en: 'Company settings', fr: 'Société' } },
          },
          {
            title: { ar: 'الصلاحيات', en: 'Permissions', fr: 'Droits' },
            body: {
              ar: 'أنشئ أدواراً (محاسب، كاشير…) وامنح كل دور الشاشات المناسبة فقط.',
              en: 'Create roles (accountant, cashier…) and grant only needed screens.',
              fr: 'Créez des rôles et n’attribuez que les écrans nécessaires.',
            },
            shot: { motif: 'list', caption: { ar: 'الأدوار والمستخدمون', en: 'Roles & users', fr: 'Rôles' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/production/boms', '/production/boms*', '/production/orders', '/production/orders*'],
    title: { ar: 'الإنتاج (الوصفات والأوامر)', en: 'Production (BOMs & orders)', fr: 'Production' },
    summary: {
      ar: 'فعّل الوصفة ثم نفّذ الأمر: سحب مواد ← استلام صالح/تالف ← إكمال. اضغط (?) في الصفحة للتفاصيل.',
      en: 'Activate the BOM then run the order: issue → good/scrap receive → complete. Click (?) on the page for details.',
      fr: 'Activer BOM puis OF : sortie → entrée/rebut → clôture. Aide (?) sur la page.',
    },
    emoji: '🏭',
    accent: '#0e7490',
    sections: [
      {
        title: { ar: 'مسار الإنتاج', en: 'Production path', fr: 'Parcours' },
        steps: [
          {
            title: { ar: 'وصفة مفعّلة', en: 'Active BOM', fr: 'BOM active' },
            body: {
              ar: 'أنشئ الوصفة (مواد، فاقد، بدائل)، احفظ مسودة، ثم فعّل مباشرة أو عبر الاعتماد. بدون تفعيل لن تظهر في الأوامر.',
              en: 'Create BOM (materials, waste, alternatives), save Draft, then activate (quick or approval). Without Active it will not appear on orders.',
              fr: 'Créer BOM, activer. Sans activation, absente des OF.',
            },
            shot: { motif: 'form', caption: { ar: 'شاشة الوصفة', en: 'BOM screen', fr: 'Nomenclature' } },
          },
          {
            title: { ar: 'أمر وتنفيذ', en: 'Order & execution', fr: 'OF' },
            body: {
              ar: 'أنشئ أمراً، اسحب المواد (↓)، استلم صالحاً وتالفاً (↑)، ثم أكمل. الإلغاء يعكس المخزون.',
              en: 'Create order, issue (↓), receive good/scrap (↑), then complete. Cancel reverses stock.',
              fr: 'OF, sortie, réception/rebut, clôture; annulation inverse le stock.',
            },
            shot: { motif: 'approve', caption: { ar: 'سحب واستلام', en: 'Issue & receive', fr: 'Sortie et entrée' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/stores', '/categories', '/inventory*', '/opening-stock*', '/branch-transfers*', '/stock-adjustment*'],
    title: { ar: 'المخازن والمخزون', en: 'Stores & inventory', fr: 'Dépôts et stock' },
    summary: {
      ar: 'إدارة المخازن، الفئات، التسويات، والمناقلات للحفاظ على أرصدة دقيقة.',
      en: 'Manage stores, categories, adjustments, and transfers for accurate stock.',
      fr: 'Dépôts, catégories, ajustements et transferts pour un stock fiable.',
    },
    emoji: '🏪',
    accent: '#065f46',
    sections: [
      {
        title: { ar: 'ضبط المخزون', en: 'Keep stock accurate', fr: 'Fiabiliser le stock' },
        steps: [
          {
            title: { ar: 'المخازن والفئات', en: 'Stores & categories', fr: 'Dépôts et catégories' },
            body: {
              ar: 'أنشئ المخازن أولاً، ثم صنّف الأصناف ضمن فئات لتسهيل التقارير.',
              en: 'Create stores first, then group items into categories for reporting.',
              fr: 'Créez les dépôts, puis classez les articles.',
            },
            shot: { motif: 'list', caption: { ar: 'قائمة المخازن', en: 'Stores list', fr: 'Liste dépôts' } },
          },
          {
            title: { ar: 'تسوية أو مناقلة', en: 'Adjust or transfer', fr: 'Ajuster ou transférer' },
            body: {
              ar: 'استخدم التسوية لتصحيح الجرد، والمناقلة لنقل كمية بين فرعين أو مخزنين.',
              en: 'Use adjustments to correct counts, transfers to move qty between stores.',
              fr: 'Ajustements pour corriger, transferts pour déplacer.',
            },
            shot: { motif: 'form', caption: { ar: 'حركة مخزنية', en: 'Stock movement', fr: 'Mouvement' } },
          },
        ],
      },
    ],
  },
  {
    match: ['/fixed-assets', '/fixed-assets*'],
    title: { ar: 'الأصول الثابتة', en: 'Fixed assets', fr: 'Immobilisations' },
    summary: {
      ar: 'تسجيل الأصول واحتساب الإهلاك وربطها بالفواتير عند الشراء.',
      en: 'Register assets, run depreciation, and link purchases when buying assets.',
      fr: 'Immobilisations, amortissement et lien achats.',
    },
    emoji: '🏢',
    accent: '#334155',
    sections: [
      {
        title: { ar: 'دورة الأصل', en: 'Asset lifecycle', fr: 'Cycle de vie' },
        steps: [
          {
            title: { ar: 'تسجيل أصل', en: 'Register an asset', fr: 'Enregistrer' },
            body: {
              ar: 'أدخل الاسم، تاريخ الاقتناء، التكلفة، والعمر الإنتاجي وطريقة الإهلاك.',
              en: 'Enter name, acquisition date, cost, useful life, and depreciation method.',
              fr: 'Nom, date d’acquisition, coût, durée et méthode d’amortissement.',
            },
            shot: { motif: 'form', caption: { ar: 'بطاقة الأصل', en: 'Asset card', fr: 'Fiche' } },
          },
          {
            title: { ar: 'تشغيل الإهلاك', en: 'Run depreciation', fr: 'Amortir' },
            body: {
              ar: 'نفّذ قيد الإهلاك الدوري من شاشة الأصول حسب الفترة المحددة.',
              en: 'Post periodic depreciation from the assets screen for the period.',
              fr: 'Passez l’amortissement périodique depuis l’écran actifs.',
            },
            shot: { motif: 'approve', caption: { ar: 'قيد الإهلاك', en: 'Depreciation entry', fr: 'Écriture' } },
          },
        ],
      },
    ],
  },
]
